import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { trackedFunction } from 'reactiveweb/function';
import { VStack } from 'tio-ui/components/layout';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import Chip from 'tio-ui/components/chip';
import Button from 'tio-ui/components/button';
import { eq } from 'tio-ui/utilities';
import type Store from '@ember-data/store';
import { OpenSearchQueryBuilder } from 'tio-common/utils/open-search-builder';
import { OpenSearchResponseBuilder } from 'tio-common/utils/open-search-response-builder';
import { executeSearchRequest } from 'tio-common/requests/search';
import type { OpenSearchResponse } from 'tio-data/models/search';
import {
  PhUser,
  PhChartLine,
  PhTarget,
  PhCheckCircle,
  PhClock,
  PhWarning,
  PhCaretDown,
  PhCaretRight,
  PhGraduationCap,
  PhLightbulb,
} from 'ember-phosphor-icons';
import StatCard from 'tio-ui/components/stat-card';
import SectionPanel from 'tio-ui/components/section-panel';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';

type PersonIndexEmployee = {
  companyName?: string;
  payrollId?: string;
  email?: string;
  tasParticipantState?: string;
};

type PersonIndexDoc = {
  id: string;
  firstName?: string;
  lastName?: string;
  userNames?: string[];
  jurisdictions?: string[];
  employees?: PersonIndexEmployee[];
};

// Mock team data
const TEAM_DATA = {
  manager: {
    name: 'Sarah Kim',
    title: 'Nurse Manager',
    department: 'Medical-Surgical Unit',
  },
  summary: {
    totalDirectReports: 8,
    withActivePlans: 5,
    onTrack: 3,
    needsAttention: 2,
    taParticipants: 4,
    avgPlanProgress: 62,
  },
  teamMembers: [
    {
      id: '1',
      name: 'Emily Johnson',
      role: 'Staff Nurse',
      avatar: 'EJ',
      targetRole: 'Charge Nurse',
      status: 'on-track',
      planProgress: 75,
      currentCourse: 'Leadership Fundamentals',
      courseProgress: 60,
      taActive: true,
      skillsGained: 3,
      skillsRemaining: 2,
      nextMilestone: 'Complete leadership course',
      nextMilestoneDate: 'Feb 2026',
    },
    {
      id: '2',
      name: 'Michael Chen',
      role: 'Staff Nurse',
      avatar: 'MC',
      targetRole: 'ICU Specialist',
      status: 'on-track',
      planProgress: 45,
      currentCourse: 'Critical Care Certification',
      courseProgress: 30,
      taActive: true,
      skillsGained: 2,
      skillsRemaining: 4,
      nextMilestone: 'Pass CCRN exam',
      nextMilestoneDate: 'Apr 2026',
    },
    {
      id: '3',
      name: 'Jessica Williams',
      role: 'Senior Nurse',
      avatar: 'JW',
      targetRole: 'Nurse Manager',
      status: 'stalled',
      planProgress: 35,
      currentCourse: 'Healthcare Administration',
      courseProgress: 10,
      taActive: true,
      skillsGained: 1,
      skillsRemaining: 5,
      nextMilestone: 'Resume coursework',
      nextMilestoneDate: 'Overdue',
      alert: 'No activity in 45 days',
    },
    {
      id: '4',
      name: 'David Thompson',
      role: 'Staff Nurse',
      avatar: 'DT',
      targetRole: 'Clinical Educator',
      status: 'on-track',
      planProgress: 88,
      currentCourse: 'Teaching Methods',
      courseProgress: 95,
      taActive: true,
      skillsGained: 5,
      skillsRemaining: 1,
      nextMilestone: 'Apply for educator role',
      nextMilestoneDate: 'Jan 2026',
    },
    {
      id: '5',
      name: 'Amanda Garcia',
      role: 'Staff Nurse',
      avatar: 'AG',
      targetRole: null,
      status: 'no-plan',
      planProgress: 0,
      currentCourse: null,
      courseProgress: 0,
      taActive: false,
      skillsGained: 0,
      skillsRemaining: 0,
      nextMilestone: 'Create career plan',
      nextMilestoneDate: null,
    },
    {
      id: '6',
      name: 'Robert Lee',
      role: 'Staff Nurse',
      avatar: 'RL',
      targetRole: 'Nurse Practitioner',
      status: 'at-risk',
      planProgress: 20,
      currentCourse: 'BSN Program',
      courseProgress: 25,
      taActive: false,
      skillsGained: 1,
      skillsRemaining: 8,
      nextMilestone: 'Enroll in TA program',
      nextMilestoneDate: 'Pending',
      alert: 'Not using tuition assistance',
    },
  ],
  skillsGaps: [
    { skill: 'Leadership', count: 5, priority: 'high' },
    { skill: 'Epic Super User', count: 4, priority: 'medium' },
    { skill: 'Critical Care', count: 3, priority: 'high' },
    { skill: 'Data Analytics', count: 6, priority: 'low' },
  ],
};

export default class CareerPathwaysAdminTeam extends Component {
  @service declare store: Store;
  @service declare reimaginedData: ReimaginedDataService;

  @tracked expandedMembers = new Set(['1', '3', '5']);

  get data() {
    return TEAM_DATA;
  }

  peopleIndex = trackedFunction(this, async () => {
    if (this.reimaginedData.isDemoMode) {
      return [];
    }

    const builder = new OpenSearchQueryBuilder().setPagination(1, 8);
    builder.exists('id');

    const query = {
      'model-type': 'Person',
      'search-body': builder.build(),
    };

    const { content: response } = (await this.store.request(executeSearchRequest(query))) as {
      content: OpenSearchResponse<PersonIndexDoc>;
    };

    const responseBuilder = new OpenSearchResponseBuilder<PersonIndexDoc>(response);
    return responseBuilder.getDocuments();
  });

  get indexPeople() {
    return this.peopleIndex.value ?? [];
  }

  missingFieldsFor = (person: PersonIndexDoc) => {
    const employees = person.employees ?? [];
    const hasCompany = employees.some((employee) => Boolean(employee.companyName));
    const hasPayrollId = employees.some((employee) => Boolean(employee.payrollId));
    const hasEmail = employees.some((employee) => Boolean(employee.email));
    const hasTasState = employees.some((employee) => Boolean(employee.tasParticipantState));

    return [
      ...(hasCompany ? [] : ['Company']),
      ...(hasPayrollId ? [] : ['Payroll ID']),
      ...(hasEmail ? [] : ['Email']),
      ...(hasTasState ? [] : ['TA Status']),
    ];
  };

  hasMissingFields = (person: PersonIndexDoc) => this.missingFieldsFor(person).length > 0;

  toggleMember = (id: string) => {
    if (this.expandedMembers.has(id)) {
      this.expandedMembers.delete(id);
    } else {
      this.expandedMembers.add(id);
    }
    this.expandedMembers = new Set(this.expandedMembers);
  };

  isExpanded = (id: string) => {
    return this.expandedMembers.has(id);
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="w-full min-w-0 pb-12">
      {{! Header }}
      <div class="tio-row-between mb-6">
        <div>
          <Heading @level={{1}} class="app-page-title">
            Team Career Development
          </Heading>
          <Text class="tio-fg-secondary">{{this.data.manager.name}}
            -
            {{this.data.manager.department}}</Text>
        </div>
        <div class="tio-flex">
          <Button @appearance="outlined" @intent="primary">
            <PhChartLine @size={{18}} class="mr-2" />
            Team Report
          </Button>
          <Button @intent="primary">
            <PhTarget @size={{18}} class="mr-2" />
            Set Team Goals
          </Button>
        </div>
      </div>

      {{! Summary Cards }}
      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <StatCard
          @compact={{true}}
          @label="Direct Reports"
          @value={{this.data.summary.totalDirectReports}}
        />
        <StatCard
          @compact={{true}}
          @label="Active Plans"
          @value={{this.data.summary.withActivePlans}}
          @valueColor="violet"
        />
        <StatCard
          @compact={{true}}
          @label="On Track"
          @value={{this.data.summary.onTrack}}
          @valueColor="green"
        />
        <StatCard
          @compact={{true}}
          @label="Needs Attention"
          @value={{this.data.summary.needsAttention}}
          @valueColor="amber"
        />
        <StatCard
          @compact={{true}}
          @label="Using TA"
          @value={{this.data.summary.taParticipants}}
          @valueColor="teal"
        />
        <StatCard
          @compact={{true}}
          @label="Avg Progress"
          @value={{this.data.summary.avgPlanProgress}}
          @suffix="%"
        />
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {{! Team Members List }}
        <div class="lg:col-span-2">
          <SectionPanel @icon={{PhUser}} @title="Team Members" @iconColor="violet">
            <:body>
              <div class="space-y-3">
                {{#each this.data.teamMembers as |member|}}
                  <div class="border tio-border-default rounded-lg overflow-hidden">
                    {{! Member Header - Always visible }}
                    <button
                      type="button"
                      class="w-full p-4 tio-row-loose hover:tio-bg-sunken dark:hover:bg-gray-700/50 transition-colors text-left"
                      {{on "click" (fn this.toggleMember member.id)}}
                    >
                      <div class="shrink-0">
                        {{#if (this.isExpanded member.id)}}
                          <PhCaretDown @size={{18}} class="tio-fg-disabled" />
                        {{else}}
                          <PhCaretRight @size={{18}} class="tio-fg-disabled" />
                        {{/if}}
                      </div>
                      <div
                        class="w-10 h-10 rounded-full tio-bg-brand-soft tio-row-center tio-fg-brand font-semibold"
                      >
                        {{member.avatar}}
                      </div>
                      <div class="tio-fill-min">
                        <div class="tio-row-tight">
                          <Text @weight="semibold">{{member.name}}</Text>
                          {{#if (eq member.status "on-track")}}
                            <Chip @intent="success" @size="sm">On Track</Chip>
                          {{else if (eq member.status "stalled")}}
                            <Chip @intent="warning" @size="sm">Stalled</Chip>
                          {{else if (eq member.status "at-risk")}}
                            <Chip @intent="danger" @size="sm">At Risk</Chip>
                          {{else}}
                            <Chip @intent="default" @size="sm">No Plan</Chip>
                          {{/if}}
                        </div>
                        <Text @size="sm" class="tio-fg-muted">{{member.role}}
                          {{#if member.targetRole}}
                            →
                            {{member.targetRole}}
                          {{/if}}
                        </Text>
                      </div>
                      <div class="text-right">
                        <Text @weight="semibold">{{member.planProgress}}%</Text>
                        <Text @size="sm" class="tio-fg-muted">progress</Text>
                      </div>
                    </button>

                    {{! Expanded Details }}
                    {{#if (this.isExpanded member.id)}}
                      <div
                        class="px-4 pb-4 pt-2 tio-bg-sunken dark:bg-gray-900/50 border-t tio-border-default"
                      >
                        {{#if member.alert}}
                          <div
                            class="tio-row-tight p-3 tio-bg-warning-soft border tio-border-warning rounded-lg mb-4"
                          >
                            <PhWarning @size={{18}} class="tio-fg-warning" />
                            <Text class="tio-fg-warning-h">{{member.alert}}</Text>
                          </div>
                        {{/if}}

                        <div class="grid grid-cols-2 gap-4">
                          <div>
                            <Text @size="sm" class="tio-fg-muted mb-1">Current Course</Text>
                            {{#if member.currentCourse}}
                              <div class="tio-row-tight">
                                <PhGraduationCap @size={{16}} class="tio-fg-brand" />
                                <Text @weight="medium">{{member.currentCourse}}</Text>
                              </div>
                              <div class="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div class="h-full tio-bg-brand-accent rounded-full w-1/2"></div>
                              </div>
                            {{else}}
                              <Text class="tio-fg-muted">No active course</Text>
                            {{/if}}
                          </div>
                          <div>
                            <Text @size="sm" class="tio-fg-muted mb-1">Skills Progress</Text>
                            <div class="tio-row-tight">
                              <PhLightbulb @size={{16}} class="tio-fg-info" />
                              <Text>{{member.skillsGained}}
                                gained,
                                {{member.skillsRemaining}}
                                remaining</Text>
                            </div>
                          </div>
                          <div>
                            <Text @size="sm" class="tio-fg-muted mb-1">Next Milestone</Text>
                            <div class="tio-row-tight">
                              <PhTarget @size={{16}} class="tio-fg-warning" />
                              <Text>{{member.nextMilestone}}</Text>
                            </div>
                            {{#if member.nextMilestoneDate}}
                              <Text
                                @size="sm"
                                class="tio-fg-muted ml-6"
                              >{{member.nextMilestoneDate}}</Text>
                            {{/if}}
                          </div>
                          <div>
                            <Text @size="sm" class="tio-fg-muted mb-1">TA Status</Text>
                            <div class="tio-row-tight">
                              {{#if member.taActive}}
                                <PhCheckCircle @size={{16}} class="tio-fg-success" />
                                <Text class="tio-fg-success-body">Active</Text>
                              {{else}}
                                <PhClock @size={{16}} class="tio-fg-disabled" />
                                <Text class="tio-fg-muted">Not enrolled</Text>
                              {{/if}}
                            </div>
                          </div>
                        </div>

                        <div class="tio-flex-tight mt-4">
                          <Button @appearance="outlined" @size="sm">View Full Plan</Button>
                          <Button @appearance="outlined" @size="sm">Schedule 1:1</Button>
                          {{#if (eq member.status "no-plan")}}
                            <Button @intent="primary" @size="sm">Invite to Create Plan</Button>
                          {{/if}}
                        </div>
                      </div>
                    {{/if}}
                  </div>
                {{/each}}
              </div>
            </:body>
          </SectionPanel>
        </div>

        {{! Person Index Coverage }}
        <div class="lg:col-span-1">
          <SectionPanel @icon={{PhWarning}} @title="Person Index Coverage" @iconColor="amber">
            <:body>
              {{#if this.peopleIndex.isLoading}}
                <Text @variant="muted">Loading people index…</Text>
              {{else if this.indexPeople.length}}
                <div class="space-y-4">
                  {{#each this.indexPeople as |person|}}
                    <div class="rounded-lg border tio-border-default p-4">
                      <div class="tio-row-start-between gap-2">
                        <div>
                          <Text @weight="semibold">
                            {{person.firstName}}
                            {{person.lastName}}
                          </Text>
                        </div>
                        <Chip
                          @intent={{if (this.hasMissingFields person) "warning" "success"}}
                          @size="sm"
                        >
                          {{if (this.hasMissingFields person) "Needs Data" "Complete"}}
                        </Chip>
                      </div>
                      {{#if (this.hasMissingFields person)}}
                        <div class="tio-wrap-tight mt-3">
                          {{#each (this.missingFieldsFor person) as |field|}}
                            <Chip @intent="warning" @size="sm">Missing {{field}}</Chip>
                          {{/each}}
                        </div>
                      {{else}}
                        <Text @variant="muted" @size="xs" class="mt-3">All core fields present.</Text>
                      {{/if}}
                    </div>
                  {{/each}}
                </div>
              {{else}}
                <Text @variant="muted">No people returned from the index.</Text>
              {{/if}}
            </:body>
          </SectionPanel>
        </div>

        {{! Sidebar }}
        <div class="space-y-6">
          {{! Team Skills Gaps }}
          <SectionPanel @icon={{PhLightbulb}} @title="Team Skills Gaps" @iconColor="amber">
            <:body>
              <div class="space-y-3">
                {{#each this.data.skillsGaps as |gap|}}
                  <div class="tio-row-between p-3 tio-bg-sunken dark:bg-gray-900/50 rounded-lg">
                    <div>
                      <Text @weight="medium">{{gap.skill}}</Text>
                      <Text @size="sm" class="tio-fg-muted">{{gap.count}}
                        team members</Text>
                    </div>
                    {{#if (eq gap.priority "high")}}
                      <Chip @intent="danger" @size="sm">High</Chip>
                    {{else if (eq gap.priority "medium")}}
                      <Chip @intent="warning" @size="sm">Medium</Chip>
                    {{else}}
                      <Chip @intent="default" @size="sm">Low</Chip>
                    {{/if}}
                  </div>
                {{/each}}
              </div>
            </:body>
          </SectionPanel>

          {{! Quick Actions }}
          <SectionPanel @icon={{PhTarget}} @title="Quick Actions" @iconColor="gray">
            <:body>
              <div class="space-y-2">
                <Button @appearance="outlined" class="w-full justify-start">
                  <PhChartLine @size={{18}} class="mr-2" />
                  Download Team Report
                </Button>
                <Button @appearance="outlined" class="w-full justify-start">
                  <PhTarget @size={{18}} class="mr-2" />
                  Set Development Goals
                </Button>
                <Button @appearance="outlined" class="w-full justify-start">
                  <PhGraduationCap @size={{18}} class="mr-2" />
                  Recommend Programs
                </Button>
              </div>
            </:body>
          </SectionPanel>
        </div>
      </div>
    </VStack>
  </template>
}
